import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import type { ApolloCache, ApolloLink, Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import { uniqWith } from 'ramda';
import { Apollo } from 'src/gql/generated';
import { workflowTypename } from 'src/helpers';

const phasesKey: keyof Apollo.Workflow = 'phases';

type PhaseSigningFlagFragment = Pick<Apollo.Phase, 'id'> & {
  __typename: Apollo.NonSigningPhase['__typename'] | Apollo.SigningPhase['__typename'];
};

export function useGetPhases(workflowId: UUID | null) {
  const { data, loading } = Apollo.useGetWorkflowPhasesQuery({
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
  });
  return {
    loading,
    phases: data?.workflow?.phases ?? [],
  };
}

export function useCreatePhase(): {
  createPhase: (
    options: Parameters<Apollo.CreatePhaseMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.CreatePhaseMutation>>;
  loading: boolean;
} {
  const [createPhase, { loading }] = Apollo.useCreatePhaseMutation();
  const safeCreatePhase = useCallback(
    (options: Parameters<Apollo.CreatePhaseMutationHookResult[0]>[0]) => {
      const workflowId = options.variables?.input.workflowId;
      return createPhase({
        ...options,
        update(cache, { data }) {
          const newPhase = data?.createPhase?.phase;
          if (!newPhase || !workflowId) return;

          const newPhaseRef = cache.writeFragment({
            data: newPhase,
            fragment: gql`
              fragment NewPhase on Phase {
                __typename
                id
              }
            `,
          });
          if (!newPhaseRef) return;

          cache.modify({
            fields: {
              [phasesKey]: (existingRefs: readonly Reference[] = [], { readField }) => {
                return uniqWith(
                  (a, b) => readField<UUID>('id', a) === readField<UUID>('id', b),
                  [...existingRefs, newPhaseRef]
                );
              },
            },
            id: cache.identify({ __typename: workflowTypename, id: workflowId }),
          });
        },
      });
    },
    [createPhase]
  );

  return {
    createPhase: safeCreatePhase,
    loading,
  };
}

/**
 * No manual cache update needed — the mutation returns PhaseSimple which has all
 * stored fields, so Apollo's normalized cache updates the Phase record automatically.
 */
export function useUpdatePhase(): {
  loading: boolean;
  updatePhase: (
    options: Parameters<Apollo.UpdatePhaseMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.UpdatePhaseMutation>>;
} {
  const [updatePhase, { loading }] = Apollo.useUpdatePhaseMutation();
  return {
    loading,
    updatePhase,
  };
}

export function useDeletePhase(): {
  deletePhase: (
    phaseId: UUID,
    workflowId: UUID
  ) => Promise<ApolloLink.Result<Apollo.DeletePhaseMutation>>;
  loading: boolean;
} {
  const [deletePhase, { loading }] = Apollo.useDeletePhaseMutation();
  const safeDeletePhase = useCallback(
    (phaseId: UUID, workflowId: UUID) => {
      return deletePhase({
        refetchQueries: [
          {
            query: Apollo.GetWorkflowPhasesDocument,
            variables: { workflowId },
          },
        ],
        update(cache) {
          const phaseCacheId = cache.identify({ __typename: 'SigningPhase', id: phaseId });
          const deletedPhase = cache.readFragment<PhaseSigningFlagFragment>({
            fragment: gql`
              fragment PhaseSigningFlag on Phase {
                __typename
                id
              }
            `,
            id: phaseCacheId,
          });
          cache.modify({
            fields: {
              [phasesKey]: (existingRefs: readonly Reference[] = [], { readField }) =>
                existingRefs.filter(ref => readField<UUID>('id', ref) !== phaseId),
            },
            id: cache.identify({ __typename: workflowTypename, id: workflowId }),
          });

          if (deletedPhase?.__typename === 'SigningPhase')
            demoteSigningWorkflowFilesToGeneralInCache(cache, workflowId);

          cache.evict({ id: phaseCacheId });
          cache.gc();
        },
        variables: { phaseId },
      });
    },
    [deletePhase]
  );

  return {
    deletePhase: safeDeletePhase,
    loading,
  };
}

/**
 * Replaces the phases list on the Workflow entity with the ordered refs returned
 * by the server so the list reflects the new order immediately.
 */
export function useReorderPhases(): {
  loading: boolean;
  reorderPhases: (
    options: Parameters<Apollo.ReorderPhasesMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.ReorderPhasesMutation>>;
} {
  const [reorderPhases, { loading }] = Apollo.useReorderPhasesMutation();
  const safeReorderPhases = useCallback(
    (options: Parameters<Apollo.ReorderPhasesMutationHookResult[0]>[0]) => {
      const workflowId = options.variables?.input.workflowId;
      return reorderPhases({
        ...options,
        update(cache, { data }) {
          const phases = data?.reorderPhases;
          if (!phases?.length || !workflowId) return;

          const orderedRefs = phases
            .map(phase =>
              cache.writeFragment({
                data: phase,
                fragment: gql`
                  fragment PhaseRef on Phase {
                    __typename
                    id
                  }
                `,
              })
            )
            .filter((ref): ref is Reference => ref != null);

          cache.modify({
            fields: {
              [phasesKey]: () => orderedRefs,
            },
            id: cache.identify({ __typename: workflowTypename, id: workflowId }),
          });
        },
      });
    },
    [reorderPhases]
  );

  return {
    loading,
    reorderPhases: safeReorderPhases,
  };
}

function demoteSigningWorkflowFilesToGeneralInCache(cache: ApolloCache, workflowId: UUID) {
  const workflowCacheId = cache.identify({ __typename: workflowTypename, id: workflowId });
  cache.modify({
    fields: {
      files(existingRefs: readonly Reference[] = [], { readField }) {
        existingRefs.forEach(ref => {
          const fileId = readField<UUID>('id', ref);
          const role = readField('documentRole', ref);
          if (role === Apollo.WorkflowFileDocumentRole.Signing && fileId) {
            cache.modify({
              fields: {
                documentRole() {
                  return Apollo.WorkflowFileDocumentRole.General;
                },
                phaseId() {
                  return null;
                },
              },
              id: cache.identify({ __typename: 'WorkflowFile', id: fileId }),
            });
          }
        });
        return existingRefs;
      },
    },
    id: workflowCacheId,
  });
}
